import * as Sentry from '@sentry/react';

/**
 * Fast check: uses JSON.stringify with a replacer to detect whether any
 * undefined value exists anywhere in the data object. JSON.stringify visits
 * every value exactly once, so this is a single O(n) pass with no object
 * allocation — much cheaper than a full recursive walk.
 *
 * Returns true when at least one undefined was found, false otherwise.
 */
const hasUndefined = (jsonObj: unknown): boolean => {
  let found = false;
  try {
    JSON.stringify(jsonObj, (_key, val) => {
      if (val === undefined) {
        found = true;
      }
      return val;
    });
  } catch {
    // If stringify throws (e.g. circular reference, getter that throws, or a
    // Firestore sentinel with a non-serializable internal), skip observation
    // entirely. The SDK's `ignoreUndefinedProperties: true` still handles the
    // actual write correctly — we just lose the dot-path report for THIS
    // payload. Returning `false` here keeps the recursive walk from running
    // on hostile input where it could stack-overflow or throw again.
    return false;
  }
  return found;
};

// Hard caps for the recursive walk in collectUndefinedPaths. The walk only
// runs when hasUndefined returned true, so this is the slow path. Bounded
// depth + cycle guard ensure a malformed payload (DocumentReference back-ref,
// user-supplied circular object, deeply-nested React state) can't crash the
// observability pass and abort the surrounding write. Real-world Firestore
// payloads are well under both limits.
const MAX_WALK_DEPTH = 32;
const MAX_PATHS_COLLECTED = 500;

/**
 * Recursively walks the value (no cloning) and records the dot-notation path
 * of every undefined field into the provided undefinedPaths array.
 *
 * The `visited` WeakSet guards against cycles (a circular ref would recurse
 * infinitely without it). The `depth` parameter caps recursion so deeply
 * nested payloads can't blow the stack even when the cycle guard catches all
 * back-edges (a long chain with no back-edges still recurses linearly).
 */
const collectUndefinedPaths = (
  jsonObj: any,
  path: string,
  undefinedPaths: string[],
  visited: WeakSet<object>,
  depth: number
): void => {
  if (depth > MAX_WALK_DEPTH || undefinedPaths.length >= MAX_PATHS_COLLECTED) {
    return;
  }

  if (jsonObj === undefined) {
    undefinedPaths.push(path);
    return;
  }

  // Primitives and Date — nothing to recurse into.
  if (jsonObj === null || typeof jsonObj !== 'object' || jsonObj instanceof Date) {
    return;
  }

  // Firestore sentinel objects (serverTimestamp, FieldValue, etc.) — don't recurse.
  if (typeof jsonObj.isEqual === 'function' || jsonObj._methodName !== undefined) {
    return;
  }

  // Cycle guard: if we've seen this object already, stop. Without this, an
  // input containing a circular ref (e.g. a DocumentReference's back-ref to
  // its parent firestore instance) would recurse forever and stack-overflow.
  if (visited.has(jsonObj)) {
    return;
  }
  visited.add(jsonObj);

  if (Array.isArray(jsonObj)) {
    for (let i = 0; i < jsonObj.length; i++) {
      collectUndefinedPaths(jsonObj[i], `${path}[${i}]`, undefinedPaths, visited, depth + 1);
    }
    return;
  }

  for (const key of Object.keys(jsonObj)) {
    collectUndefinedPaths(
      jsonObj[key],
      path ? `${path}.${key}` : key,
      undefinedPaths,
      visited,
      depth + 1
    );
  }
};

/**
 * Observability pass for Firestore writes. The SDK is initialized with
 * `ignoreUndefinedProperties: true`, so undefined fields are silently stripped
 * at the wire level — but we still want the data-integrity signal so the upstream
 * source can be tracked and fixed. This walk collects the dot-paths of every
 * undefined field and reports them to Sentry.
 *
 * Fast path: when the payload contains no undefined values, returns without
 * any allocation.
 *
 * @param data     - The raw data object about to be written to Firestore.
 * @param resource - The Firestore document/collection path (Sentry context).
 */
export const observeUndefinedFields = <T extends Record<string, any>>(
  data: T,
  resource?: string
): void => {
  if (!hasUndefined(data)) {
    return;
  }

  const undefinedPaths: string[] = [];
  collectUndefinedPaths(data, '', undefinedPaths, new WeakSet<object>(), 0);
  if (undefinedPaths.length === 0) {
    return;
  }

  // Reported at level=error because an undefined field is a data-integrity
  // failure (e.g. a student response field silently dropped from the write).
  // The SDK still completes the write, but the missing field is a real defect
  // upstream that needs tracking.
  //
  // We deliberately ship ONLY the dot-paths of the undefined fields, not the
  // payload itself. K-12 EdTech: write payloads can contain student responses,
  // grades, attempt answers, chat content — sending that to Sentry would be a
  // FERPA / COPPA / GDPR exposure. The dot-paths are enough to locate the
  // upstream caller; the payload is unnecessary for triage.
  Sentry.captureMessage('[Firestore] Undefined fields stripped before write', {
    level: 'error',
    extra: {
      resource,
      undefinedCount: undefinedPaths.length,
      undefinedFields: undefinedPaths,
    },
  });
};
